// src/utils/prompts.js
// Centraliza AQUÍ todo tu texto repetitivo y construcción de prompts ↓↓↓

/* ---------- ESQUEMA DE BASE DE DATOS COMPLETO CON FORMATOS ---------- */
export const DB_SCHEMA = `
ESQUEMA DE BASE DE DATOS CORREGIDO:

public.my_data (
  year INTEGER,                 -- Año de la venta (ej: 2024)
  month TEXT,                   -- Mes de la venta formato (aaaa-mm) (ej: '2024-01', '2024-12')
  week TEXT,                    -- Semana de la venta formato (aaaa-ww) (ej: '2024-01', '2024-52')
  date DATE,                    -- Fecha exacta de la venta (ej: '2024-01-15')
  store_id INTEGER,             -- ID único de la tienda
  desc_tienda TEXT,             -- Descripción/nombre de la tienda
  category TEXT,                -- Categoría del producto
  sku_client INTEGER,           -- SKU/código del producto del cliente
  item_desc TEXT,               -- Descripción del producto
  net_sales REAL,               -- Ventas netas en pesos
  units REAL,                   -- Unidades vendidas
  final_price REAL              -- Precio final del producto
);

public.competitors_data (
  year INTEGER,                 -- Año del dato de competencia (ej: 2024)
  month TEXT,                   -- Mes del dato formato (aaaa-mm) (ej: '2024-01', '2024-12')
  week TEXT,                    -- Semana del dato formato (aaaa-ww) (ej: '2024-01', '2024-52')
  date DATE,                    -- Fecha exacta del dato (ej: '2024-01-15')
  competitor TEXT,              -- Nombre del competidor
  category TEXT,                -- Categoría del producto
  brand TEXT,                   -- Marca del producto
  upc INTEGER,                  -- Código UPC del producto
  item_desc TEXT,               -- Descripción del producto
  price REAL,                   -- Precio del competidor
  sales_flag REAL,              -- Indicador de ventas (valor numérico)
  final_price REAL,             -- Precio final
  sku_client INTEGER            -- SKU/código del producto del cliente
);

RELACIONES PRINCIPALES:
- Ambas tablas se pueden relacionar por: sku_client, category, year, month (aaaa-mm), week (aaaa-ww), date
- my_data contiene datos internos de ventas de nuestras tiendas
- competitors_data contiene datos de precios y ventas de competidores

CAMPOS CLAVE PARA ANÁLISIS:
- Ventas: net_sales, units (solo en my_data)
- Precios: final_price (ambas tablas), price (solo competitors_data)
- Tiempo: date, year, month (aaaa-mm), week (aaaa-ww)
- Productos: sku_client, category, item_desc
- Tiendas: store_id, desc_tienda (solo en my_data)
- Competencia: competitor, brand, upc (solo en competitors_data)
- Indicadores: sales_flag (solo en competitors_data)

NOTAS IMPORTANTES PARA CONSULTAS SQL:
- year, sku_client, store_id, upc son INTEGER (no necesitan comillas)
- net_sales, units, final_price, price, sales_flag son REAL (operaciones numéricas directas)
- date es DATE (usar funciones de fecha de PostgreSQL)
- month (aaaa-mm), week (aaaa-ww) son TEXT (usar comillas en comparaciones)
- Para filtros por mes: WHERE month = '2024-01' o month IN ('2024-01', '2024-02')
- Para filtros por semana: WHERE week = '2024-15' o week BETWEEN '2024-01' AND '2024-10'
`;

/* ---------- BUILDERS DE PROMPTS ACTUALIZADOS ---------- */
export const buildWebSearchPrompt = (userQuery) => `
Eres un asistente especializado en búsqueda web que complementa a un sistema de análisis de datos para retail. Tu función es ÚNICAMENTE buscar información externa específica que NO está disponible en la base de datos interna.

ANÁLISIS DE LA CONSULTA:
Primero analiza la pregunta del usuario e identifica QUÉ información específica necesita ser buscada en internet vs qué puede ser respondido con datos internos.

INFORMACIÓN QUE DEBES BUSCAR (externa/actualizada):
- Precios de commodities y materias primas
- Tasas de cambio actuales
- Inflación y datos macroeconómicos recientes
- Noticias de mercado y eventos económicos
- Benchmarks de industria y competidores externos
- Regulaciones o cambios normativos recientes
- Tendencias globales de consumo y retail
- Datos de mercados internacionales
- Eventos que afecten precios (huelgas, desastres naturales, etc.)

INFORMACIÓN QUE NO DEBES BUSCAR (disponible internamente):
- Ventas propias (net_sales), unidades vendidas (units)
- Datos de tiendas específicas (store_id, desc_tienda)
- Precios internos y de competidores (final_price, price)
- Datos históricos por SKU, categoría, marca
- Información de productos específicos (sku_client, item_desc)
- Análisis comparativos entre nuestras ventas vs competencia
- Datos por year, month (aaaa-mm), week (aaaa-ww) o date específicos

INSTRUCCIONES:
1. Si la pregunta SOLO requiere datos internos → Responde: "No se requiere búsqueda web. El agente principal puede responder con datos internos."

2. Si la pregunta requiere información externa específica → Busca ÚNICAMENTE esa información y proporciona:
   - Datos específicos encontrados (números, fechas, valores exactos)
   - Fuente y fecha de la información
   - Contexto breve si es relevante

3. Si no encuentras la información específica → Responde: "No se pudo encontrar información actualizada sobre [tema específico]. El agente principal debe proceder solo con datos internos."

EJEMPLOS:
- "Ventas totales con precio del dólar actual" → Buscar solo: precio actual del dólar
- "Comparar nuestros precios vs inflación actual" → Buscar solo: tasa de inflación actual
- "Tendencias de ventas por tienda en month 2024-01" → Responder: "No se requiere búsqueda web"
- "Impacto del precio del petróleo en costos de transporte" → Buscar solo: precio actual del petróleo

FORMATO DE RESPUESTA:
Si encuentras información:
"INFORMACIÓN EXTERNA ENCONTRADA:
[Dato específico]: [Valor] ([Fuente], [Fecha])
[Contexto breve si es necesario]"

Sé preciso, conciso y enfócate solo en la información externa necesaria.

CONSULTA DEL USUARIO: ${userQuery}
`;

export const buildSuggestedQuestionsPrompt = (prevQ, botResp) => `
Estoy usando un asistente de análisis de datos para retail con PostgreSQL. Por favor, genera 3 preguntas de seguimiento relevantes basadas en la pregunta anterior y la respuesta obtenida.

${DB_SCHEMA}

Pregunta anterior: "${prevQ}"
Respuesta: "${botResp}"

Instrucciones:
1. Las preguntas deben ser para profundizar en el análisis y descubrir insights adicionales
2. Deben ser específicas y relacionadas con el tema de la pregunta original
3. Deben poder responderse con las tablas my_data y competitors_data
4. Enfócate en análisis de ventas, precios, competencia, tiendas, categorías o productos
5. Considera los formatos de tiempo: year (INTEGER), month (aaaa-mm), week (aaaa-ww), date
6. Deben ser breves y estar listas para copiar y pegar

TIPOS DE ANÁLISIS POSIBLES:
- Comparaciones de precios entre nosotros y competidores
- Análisis de tendencias por tiempo (year, month aaaa-mm, week aaaa-ww)
- Rendimiento por tienda o categoría
- Análisis de unidades vs ventas netas
- Comparaciones por marca o competidor específico
- Evolución semanal o mensual de métricas clave

IMPORTANTE: Responde SOLO con las 3 preguntas en formato estrictamente JSON:
["pregunta 1", "pregunta 2", "pregunta 3"]

No incluyas otra explicación, solo el array JSON.
`;

export const buildConsultClaudePrompt = (origQuery, errorMsg) => `
Eres un asistente especializado en análisis de datos para retail. El usuario está intentando 
obtener información de una base de datos PostgreSQL con el siguiente esquema:

${DB_SCHEMA}

Ha ocurrido un error con la consulta:
"${origQuery}"
Error devuelto:
"${errorMsg}"

Por favor, proporciona 2-3 alternativas para reformular mejor esta consulta. 
Para cada alternativa, proporciona:
1. Una pregunta reformulada más específica y clara
2. Una breve justificación de por qué esta alternativa podría funcionar mejor

CONTEXTO DE DATOS DISPONIBLES:
- Ventas internas: net_sales, units por tienda y producto
- Precios: final_price (nosotros y competidores), price (solo competidores)
- Información de productos: sku_client, category, item_desc
- Tiempo: date, year, month (aaaa-mm), week (aaaa-ww)
- Tiendas: store_id, desc_tienda
- Competencia: competitor, brand, upc

FORMATOS TEMPORALES:
- year: INTEGER (ej: 2024)
- month: TEXT formato aaaa-mm (ej: '2024-01', '2024-12')
- week: TEXT formato aaaa-ww (ej: '2024-01', '2024-52')
- date: DATE (ej: '2024-01-15')

Devuelve solo las alternativas en formato de lista numerada, sin introducción ni conclusión.
Cada alternativa debe ser concisa y directa, enfocándose en obtener datos relevantes que el sistema pueda procesar.
`;

export const buildSQLPrompt = (userContent, mensajesJSON) => `
Eres un agente especializado en optimizar consultas SQL para PostgreSQL basado en la respuesta de otro agente buscador,necesito que  estas consultas sirvvan  para generar tablas 
 con los datos contstando la consulta del usuario con información relevante para visualizar.

${DB_SCHEMA}

Consulta del usuario:
${userContent}

Consultas de referencia:
${mensajesJSON}

IMPORTANTE - TIPOS DE DATOS Y FORMATOS:
1. La consulta resultante debe ser una sola consulta SQL válida para PostgreSQL
2. Usa ÚNICAMENTE las tablas public.my_data y public.competitors_data
3. Para campos INTEGER (sku_client, store_id, upc): NO uses comillas
4. Para campos REAL (net_sales, units, final_price, price, sales_flag): Operaciones numéricas directas
5. Para campos TEXT (year, month, week, category, etc.): Usa comillas simples
6. Para campo DATE: Usa funciones de fecha de PostgreSQL apropiadas

REGLAS ESPECÍFICAS POR TIPO DE DATO:
- INTEGER: WHERE sku_client = 123, WHERE store_id = 456 (sin comillas)
- REAL: SUM(net_sales), AVG(final_price) (operaciones directas)
- TEXT: WHERE year = '2024', WHERE month = '2024-01' (con comillas)
- DATE: WHERE date >= '2024-01-01' o date >= CURRENT_DATE - INTERVAL '30 days'

FORMATOS TEMPORALES ESPECÍFICOS:
- year: TEXT con comillas (ej: WHERE year = '2024')
- month: TEXT formato 'aaaa-mm' (ej: WHERE month = '2024-01' o month IN ('2024-01', '2024-02'))
- week: TEXT formato 'aaaa-ww' (ej: WHERE week = '2024-15' o week BETWEEN '2024-01' AND '2024-10')
- date: DATE estándar (ej: WHERE date >= '2024-01-15')

REGLAS CRÍTICAS DE SINTAXIS:
1. Para operaciones numéricas con REAL: usa ::NUMERIC para ROUND
2. Para comparaciones entre INTEGER: directas sin CAST
3. Para JOINs entre sku_client (INTEGER): md.sku_client = cd.sku_client
4. Para fechas: CAST a DATE solo si comparas con INTERVAL
5. Para división: NULLIF(denominador, 0) para evitar división por cero
6. Para agrupaciones por tiempo: GROUP BY year, month, week (según corresponda)
7. Para ordenar por tiempo: ORDER BY year, month, week, date

REGLAS ESPECÍFICAS PARA ROUND():
- SIEMPRE usa ::NUMERIC con ROUND: ROUND(campo::NUMERIC, decimales)
- Para divisiones: ROUND((campo1::NUMERIC / NULLIF(campo2::NUMERIC, 0)), 2)
- Para promedios: ROUND(AVG(campo)::NUMERIC, 2)
- Para expresiones complejas: ROUND((expresion)::NUMERIC, 2)

EJEMPLOS DE ROUND CORRECTO:
- ROUND(final_price::NUMERIC, 2)
- ROUND(AVG(net_sales)::NUMERIC, 2)
- ROUND((net_sales::NUMERIC / NULLIF(units::NUMERIC, 0)), 2)
- ROUND((SUM(net_sales)::NUMERIC / COUNT(*)), 2)

EJEMPLOS INCORRECTOS (EVITAR):
- ROUND(final_price, 2) ❌
- ROUND(AVG(net_sales), 2) ❌
- ROUND(net_sales / units, 2) ❌

EJEMPLOS DE SINTAXIS CORRECTA:
- SELECT year, SUM(net_sales) FROM my_data WHERE year = '2024';
- SELECT month, ROUND(AVG(final_price)::NUMERIC, 2) FROM my_data WHERE month IN ('2024-01', '2024-02', '2024-03');
- SELECT week, COUNT(*) FROM competitors_data WHERE week BETWEEN '2024-01' AND '2024-10';
- SELECT * FROM my_data md JOIN competitors_data cd ON md.sku_client = cd.sku_client WHERE md.year = '2024';
- SELECT category, ROUND((SUM(net_sales)::NUMERIC / NULLIF(SUM(units)::NUMERIC, 0)), 2) as precio_promedio FROM my_data GROUP BY category;

AGRUPACIONES Y ORDENAMIENTOS COMUNES:
- Por año: GROUP BY year ORDER BY year DESC
- Por mes: GROUP BY year, month ORDER BY year DESC, month DESC  
- Por semana: GROUP BY year, week ORDER BY year DESC, week DESC
- Por fecha: GROUP BY date ORDER BY date DESC
- Combinado: GROUP BY year, month, category ORDER BY year, month, category

LÍMITES Y OPTIMIZACIÓN:
- Incluye LIMIT 10000 en consultas que puedan devolver muchos registros
- Para análisis de rendimiento, usa LIMIT apropiados (ej: TOP 10 tiendas)
- Optimiza JOINs usando los campos correctos (sku_client, category, year, month, week, date)

⚠️ CRÍTICO - FORMATO DE RESPUESTA:
- Devuelve ÚNICAMENTE el código SQL puro
- NO incluyas comentarios con # o --
- NO incluyas títulos como "Consulta SQL Optimizada"
- NO incluyas explicaciones antes o después del SQL
- NO uses formato markdown (\`\`\`sql)
- El SQL debe empezar directamente con SELECT, WITH, etc.
- USA ::NUMERIC en todas las funciones ROUND()


CONSULTA SQL:
`;